import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { useTheme } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { SxProps, keyframes } from '@mui/system';
import { motion } from 'framer-motion';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import TooltipPopup from '../commonUI/tooltips/TooltipPopup';
import AutoplayButtonPopup from './AutoPlayButtonPopup';
import {
  selectAutoPlay,
  selectAutoPlayDirection,
  setAutoPlay,
} from './imageSlice';

const pulse = keyframes`
0% {
  opacity: 1;
}
50% {
  opacity: 0.4;
}
100% {
  opacity: 1;
}
`;

interface Props {
  commonIconButtonStyles: SxProps;
  commonIconStyles: SxProps;
}

const AutoplayButton: React.FC<Props> = ({
  commonIconButtonStyles,
  commonIconStyles,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const autoPlay = useAppSelector(selectAutoPlay);
  const autoPlayDirection = useAppSelector(selectAutoPlayDirection);

  const handleClick = () => {
    dispatch(setAutoPlay(!autoPlay));
  };

  const rotationVariants = {
    forward: {
      rotate: 0,
      transition: {
        duration: 1,
        ease: 'easeInOut',
      },
    },
    back: {
      rotate: 180,
      transition: {
        duration: 1,
        ease: 'easeInOut',
      },
    },
  };

  return (
    <TooltipPopup
      colorScheme='dark'
      placement='bottom'
      popupComponent={(isTooltipOpen) => (
        <AutoplayButtonPopup isTooltipOpen={isTooltipOpen} />
      )}
    >
      <IconButton
        aria-label='Auto Play'
        size='small'
        onClick={handleClick}
        sx={{
          ...commonIconButtonStyles,
          mr: '0.2rem',
          opacity: 1,
          animation: autoPlay ? `${pulse} 2s infinite ease-in-out` : 'none',
        }}
      >
        <motion.div
          variants={rotationVariants}
          initial={rotationVariants[autoPlayDirection]}
          animate={rotationVariants[autoPlayDirection]}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}
        >
          <PlayCircleIcon
            sx={{
              ...commonIconStyles,
              color: autoPlay
                ? theme.colors.common.bloomfieldGreen
                : theme.colors.common.softWhite,
            }}
          />
        </motion.div>
      </IconButton>
    </TooltipPopup>
  );
};

export default AutoplayButton;
